import { Clock, Hash, History, SquarePen, Trash2 } from 'lucide-react';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { MetaChip } from '@/_shared/components/list/MetaChip';
import type { TableColumn } from '@/_shared/components/Table';
import { timeAgo } from '@/_shared/lib/format';
import { cn } from '@/_shared/lib/utils';

import {
  describeLastRun,
  describeTrigger,
  slackChannelId,
  slackChannelLabel,
  type WorkflowListItem,
} from '../lib/workflowList';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

interface BuildColumnsOptions {
  isAdmin: boolean;
  onDelete: (id: string) => void;
  resolveChannel: (channelId: string | null) => string | null;
}

/**
 * Columns for the workflow list's table view (header hidden). Rows are pre-sorted upstream (the toolbar
 * drives one sort for both views), so the columns are pure renderers. The identity column (title,
 * description, and the schedule · channel it runs on) mirrors the card view; status sits on its own; the
 * activity column carries the runtime state (last updated · last run); admins get an actions menu.
 */
export function buildWorkflowColumns({
  isAdmin,
  onDelete,
  resolveChannel,
}: BuildColumnsOptions): TableColumn<WorkflowListItem>[] {
  const columns: TableColumn<WorkflowListItem>[] = [
    {
      id: 'name',
      header: '',
      headerAriaLabel: 'Name, schedule, and channel',
      activatesRow: true,
      // Concise button name for the row activator — otherwise a screen reader reads the whole cell
      // (name + description + schedule + channel) as one run-on label.
      activatesRowLabel: (wf) => wf.name,
      wrap: true,
      // All-phrasing (`<span>`s) so the cell is valid content inside the Table's row-activator `<button>`.
      cell: (wf) => {
        const channel = resolveChannel(slackChannelId(wf.graph));
        return (
          <span className="block min-w-0">
            <span title={wf.name} className="block truncate font-medium text-foreground">
              {wf.name}
            </span>
            {wf.description && (
              <span title={wf.description} className="mt-0.5 line-clamp-2 text-xs font-normal text-muted-foreground">
                {wf.description}
              </span>
            )}
            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-normal text-muted-foreground">
              <MetaChip icon={Clock}>{describeTrigger(wf.trigger)}</MetaChip>
              <MetaChip icon={Hash}>{slackChannelLabel(channel)}</MetaChip>
            </span>
          </span>
        );
      },
    },
    {
      id: 'status',
      header: '',
      headerAriaLabel: 'Status',
      width: '6.5rem',
      wrap: true,
      cell: (wf) => (
        <span className="flex flex-wrap items-center gap-1">
          <WorkflowStatusBadge status={wf.status} />
          <NeedsRepairBadge listRow state={wf.accountRepair?.state ?? 'ok'} className="shrink-0" />
        </span>
      ),
    },
    {
      id: 'activity',
      header: '',
      headerAriaLabel: 'Last updated and last run',
      width: '14rem',
      wrap: true,
      cell: (wf) => {
        const lastRun = describeLastRun(wf.lastRunAt, wf.lastRunStatus);
        const rows: { icon: typeof Clock; text: string; tone?: string }[] = [
          { icon: SquarePen, text: wf.updatedAt ? `Updated ${timeAgo(wf.updatedAt)}` : '—' },
          { icon: History, text: lastRun.text, tone: lastRun.tone },
        ];
        return (
          <div className="flex flex-col gap-0.5 text-xs font-normal text-muted-foreground">
            {rows.map(({ icon: Icon, text, tone }) => (
              <div key={text} className={cn('flex items-center gap-1.5', tone)}>
                <Icon aria-hidden className="size-3.5 shrink-0" />
                <span className="truncate">{text}</span>
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  if (isAdmin) {
    columns.push({
      ...ROW_ACTIONS_COLUMN,
      cell: (wf) => (
        <KebabMenu
          label={`Actions for ${wf.name}`}
          actions={[{ label: 'Delete', icon: Trash2, destructive: true, onSelect: () => onDelete(wf.id) }]}
        />
      ),
    });
  }

  return columns;
}
